'use client';

/**
 * SlideDeckViewer — interactive navigable slide deck viewer for the
 * Profileia prospect deck (10 slides, 1280×720 16:9).
 *
 * Renders the 10 slide PNGs from /public/decks/slides/ with:
 *  - prev/next navigation (buttons, keyboard arrows, swipe on mobile)
 *  - thumbnail rail with current-slide highlight + click to jump
 *  - section + title + subtitle overlay per slide
 *  - progress bar (slide N of 10)
 *  - fullscreen toggle (uses Fullscreen API on the container)
 *  - autoplay toggle (auto-advance every 8s, pause on hover/interaction)
 *  - speaker-notes toggle (shows the embedded speaker notes for live presentation)
 *  - lazy loading (only the visible slide + neighbors are eagerly loaded)
 *
 * The PNGs + manifest.json are pre-rendered from the source slide_NN.html files
 * (see /scripts/render_slides_to_png.js). Updating the deck = re-run the
 * render script + replace the PNGs + update manifest.json.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import {
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2,
  Play,
  Pause,
  List,
  X,
  Grid3x3,
  ZoomIn,
  ZoomOut,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

export interface SlideMeta {
  n: number;
  section: string;
  title: string;
  subtitle: string;
}

interface SlideDeckViewerProps {
  slides: SlideMeta[];
  total: number;
}

const AUTOPLAY_MS = 8000;

// PR fix (Founder directive 2026-11-03): slides (WebP) + audio (MP3) are
// served from the repo (frontend/public/) — same-origin for better browser
// caching + preload behavior. Only PPTX + PDF stay on Bunny CDN (handled
// in page.tsx). This fixes: audio not playing smoothly + images not
// preloading (CORS + cache issues with cross-origin Bunny CDN).
const CDN_BASE = ''; // local paths only — no CDN for slides/audio

export function SlideDeckViewer({ slides, total }: SlideDeckViewerProps) {
  const [current, setCurrent] = useState(0); // 0-indexed
  const [thumbOpen, setThumbOpen] = useState(false);
  const [gridOpen, setGridOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [zoom, setZoom] = useState(1); // 1 = fit to width
  // P-195 (SPEC-129): eager-preload — todas las láminas se cargan al montar
  // la página. El Founder reportó que la animación de pasar láminas no era
  // fluida porque cada lámina se cargaba al navegar (lazy-loading progresivo
  // pre-fix: solo current ± 1,2,3 se pre-cargaban en goTo).
  //
  // Con este initial state, el hidden preloader (líneas más abajo) renderiza
  // las 10 láminas como imágenes invisibles de 1×1px al montar el componente
  // → browser HTTP cache las tiene listas cuando el usuario navega. Next/Image
  // dedupe + cachea las requests, así que no hay duplicación.
  //
  // LCP no se ve afectado: la lámina 1 sigue teniendo priority=true (línea 241)
  // y se renderiza visiblemente. Las otras 9 se cargan en background sin
  // bloquear el render.
  const [loadedRange, setLoadedRange] = useState<Set<number>>(
    () => new Set(Array.from({ length: total }, (_, i) => i)),
  );
  // QA-E2E-016 fix (Sprint 80): track which slides have already been loaded
  // at least once. Once a slide's onLoad fires, we add it to this Set.
  // On subsequent navigations to that slide, we skip showing the skeleton
  // (image is in browser HTTP cache → appears instantly). Pre-fix: every
  // goTo() reset imageLoaded to false, causing a skeleton spinner flash
  // even for cached images. Post-fix: only show skeleton for FIRST load
  // of each slide.
  const [everLoaded, setEverLoaded] = useState<Set<number>>(() => new Set([0]));
  const [imageLoaded, setImageLoaded] = useState(false); // tracks current slide's onLoad

  // ─── AUDIO AUTOPLAY (SPEC: audio por lámina con autoplay) ───────────────
  // El Founder directive: "Quiero agregar a cada Lamina de la presentación un
  // audio que se reproduzca en automatico una vez cambie de diapositiva, ese
  // audio explica el negocio en automatico. Para la primer diapositiva puede
  // empezar el audio apenas baja el usuario a la diapositiva o cuando cargue
  // el sitio 5 segundos despues."
  //
  // Estado:
  //   - audioEnabled: si el usuario activó el audio (false = silenciado/parado)
  //   - isMuted: mute toggle (independiente de audioEnabled)
  //   - audioVolume: 0-1
  //   - hasUserInteracted: requerido por autoplay policies de Chrome/Safari.
  //     Mientras no haya interacción, no se puede hacer play(). El 5s delay
  //     en slide 1 dispara un "fake interaction" via setTimeout + audio.play()
  //     (catch que ignora error de política autoplay).
  const [audioEnabled, setAudioEnabled] = useState(true); // default ON
  const [isMuted, setIsMuted] = useState(false);
  const [audioVolume, setAudioVolume] = useState(1);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hasUserInteractedRef = useRef(false);

  // Source audio URL — derived from current slide index (0-indexed → +1 = lamina-N.mp3)
  // P-382: served from Bunny CDN when NEXT_PUBLIC_BUNNY_CDN_HOSTNAME is set.
  const audioSrc = `${CDN_BASE}/audio/plan-de-compensacion/lamina-${current + 1}.mp3`;

  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number | null>(null);

  const goTo = useCallback(
    (idx: number) => {
      const clamped = Math.max(0, Math.min(total - 1, idx));
      setCurrent(clamped);
      // QA-E2E-016 fix: only show skeleton if this slide has NEVER been loaded
      // before. If it was already loaded (in browser HTTP cache from the
      // hidden preloader or a previous navigation), skip the skeleton —
      // the image appears instantly and showing a spinner would cause a
      // visual flash. Pre-fix: always setImageLoaded(false) → skeleton flash
      // on every navigation, even for cached images.
      if (everLoaded.has(clamped)) {
        setImageLoaded(true);
      } else {
        setImageLoaded(false);
      }
      // Preload this slide + its immediate neighbors for instant navigation.
      setLoadedRange((prev) => {
        const next = new Set(prev);
        next.add(clamped);
        next.add(clamped - 1);
        next.add(clamped + 1);
        next.add(clamped - 2);
        next.add(clamped + 2);
        next.add(clamped - 3);
        next.add(clamped + 3);
        return next;
      });
    },
    [total, everLoaded],
  );

  const goNext = useCallback(() => goTo(current + 1), [current, goTo]);
  const goPrev = useCallback(() => goTo(current - 1), [current, goTo]);

  // P-242 (SPEC-143): `toggleFullscreen` was declared AFTER the keyboard
  // navigation `useEffect` below that uses it (line 147 inside the switch),
  // triggering `no-use-before-define`. Moved the declaration ABOVE the
  // keyboard useEffect so the function is in scope before its first use.
  // Wrapped in `useCallback` so the keyboard useEffect's deps array can
  // reference it without re-binding the listener on every render.
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.().catch(() => {
        /* user denied or unsupported */
      });
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input
      if ((e.target as HTMLElement)?.tagName?.match(/INPUT|TEXTAREA/)) return;

      switch (e.key) {
        case 'ArrowRight':
        case 'PageDown':
          e.preventDefault();
          goNext();
          break;
        case 'ArrowLeft':
        case 'PageUp':
          e.preventDefault();
          goPrev();
          break;
        case 'Home':
          e.preventDefault();
          goTo(0);
          break;
        case 'End':
          e.preventDefault();
          goTo(total - 1);
          break;
        case ' ':
        case 'Spacebar':
          e.preventDefault();
          setIsPlaying((p) => !p);
          break;
        case 'f':
        case 'F':
          toggleFullscreen();
          break;
        case 'g':
        case 'G':
          setGridOpen((g) => !g);
          break;
        default:
          // Number keys 1-9 jump to slide N
          if (/^[1-9]$/.test(e.key)) {
            const n = parseInt(e.key, 10);
            goTo(n - 1);
          }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev, goTo, total, toggleFullscreen]);

  // Autoplay
  // SPEC: cuando el audio está activo (audioEnabled=true), el autoplay de
  // slides se pausa — el auto-advance lo dispara handleAudioEnded (cuando
  // termina el audio de la lámina actual). Si el audio está desactivado,
  // el autoplay clásico cada 8s funciona normalmente.
  useEffect(() => {
    if (!isPlaying) return;
    if (audioEnabled) return; // pause slide autoplay while audio plays
    const id = setInterval(() => {
      setCurrent((c) => {
        const next = c + 1;
        return next >= total ? 0 : next; // loop
      });
    }, AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [isPlaying, total, audioEnabled]);

  // ─── AUDIO AUTOPLAY — efects (SPEC: audio por lámina con autoplay) ─────
  //
  // Efecto 1:Cuando el usuario hace click/tecla por primera vez en cualquier
  // parte de la página, marcamos `hasUserInteracted=true` para que el audio
  // pueda hacer play() (Chrome/Safari autoplay policies requieren interacción).
  useEffect(() => {
    const markInteracted = () => {
      hasUserInteractedRef.current = true;
      // Cleanup one-shot
      window.removeEventListener('click', markInteracted);
      window.removeEventListener('keydown', markInteracted);
      window.removeEventListener('touchstart', markInteracted);
    };
    window.addEventListener('click', markInteracted);
    window.addEventListener('keydown', markInteracted);
    window.addEventListener('touchstart', markInteracted);
    return () => {
      window.removeEventListener('click', markInteracted);
      window.removeEventListener('keydown', markInteracted);
      window.removeEventListener('touchstart', markInteracted);
    };
  }, []);

  // Efecto 2: AUTOSTART 5s después del primer render (slide 1).
  // El Founder directive: "para la primer diapositiva puede empezar el audio
  // apenas baja el usuario a la diapositiva o cuando cargue el sitio 5 segundos
  // despues". Usamos 5s para cumplir autoplay policies (intentamos play() a
  // los 5s — si no hay interacción, el navegador bloquea + el effect se
  // reintentará cuando `hasUserInteracted` cambie).
  const [audioStartAttempted, setAudioStartAttempted] = useState(false);

  useEffect(() => {
    if (!audioEnabled) return;
    if (audioStartAttempted) return;

    const timer = setTimeout(() => {
      setAudioStartAttempted(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, [audioEnabled, audioStartAttempted]);

  // Efecto 3: Cambiar audioSource al navegar slides + hacer play().
  // Trigger cuando `current` cambia (navegación prev/next/swipe/keyboard) o
  // cuando audioStartAttempted cambia (después del 5s inicial).
  useEffect(() => {
    if (!audioRef.current) return;
    if (!audioEnabled) {
      audioRef.current.pause();
      return;
    }
    audioRef.current.muted = isMuted;
    audioRef.current.volume = audioVolume;

    // Si source cambió, reset + cargar nuevo
    if (audioRef.current.src !== audioSrc) {
      audioRef.current.src = audioSrc;
      audioRef.current.load();
    }

    // Intentar play() — catch autoplay rejection
    const playPromise = audioRef.current.play();
    if (playPromise !== undefined) {
      playPromise.catch((err) => {
        // Autoplay blocked — set state to paused, user must click play
        if (err.name === 'NotAllowedError') {
          setAudioEnabled(false);
        }
      });
    }
  }, [audioSrc, audioEnabled, isMuted, audioVolume, audioStartAttempted]);

  // Handler para cuando el audio termina (auto-advance al siguiente slide
  // cuando termina — UX fluida para presentación)
  const handleAudioEnded = useCallback(() => {
    if (current < total - 1) {
      setCurrent((c) => c + 1);
    }
  }, [current, total]);

  // Fullscreen tracking
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // Touch swipe for mobile
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    const threshold = 50;
    if (delta > threshold) goPrev();
    else if (delta < -threshold) goNext();
    touchStartX.current = null;
  };

  const currentSlide = slides[current];
  const progress = ((current + 1) / total) * 100;

  return (
    <div
      ref={containerRef}
      className="relative bg-slate-950 rounded-xl border border-slate-800 overflow-hidden select-none"
      style={{ aspectRatio: '16 / 10' }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* ─── AUDIO ELEMENT (hidden) ─────────────────────────────────────
          SPEC: audio por lámina. El <audio> element está montado siempre
          (no se desmonta entre slides — el effect cambia el src + hace
          load()). Controls custom están en la toolbar abajo.
          preload="auto" para que el browser cachee el primer audio. */}
      <audio
        ref={audioRef}
        src={audioSrc}
        onEnded={handleAudioEnded}
        preload="auto"
        className="hidden"
        aria-label={`Audio explicativo lámina ${current + 1}`}
      />

      {/* ─── MAIN STAGE ─────────────────────────────────────────────────── */}
      <div className="relative w-full h-full flex items-center justify-center bg-slate-950">
        {/* Progress bar (top) */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-slate-900 z-20">
          <div
            className="h-full bg-gradient-to-r from-blue-500 via-cyan-400 to-emerald-400 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Slide image — uses Image with priority for the current slide */}
        <div
          className="relative w-full h-full flex items-center justify-center transition-transform duration-200"
          style={{ transform: `scale(${zoom})` }}
        >
          {/* Skeleton placeholder while image loads — always rendered behind
              the Image so the user sees a smooth placeholder instead of a
              blank stage. The skeleton is hidden via opacity once the image
              is loaded (onLoad handler). This eliminates the "blank flash"
              on first load + on slide change. */}
          <div
            className="absolute inset-0 flex items-center justify-center bg-slate-900/60 animate-pulse transition-opacity duration-300"
            aria-hidden
            style={{ opacity: imageLoaded ? 0 : 1 }}
          >
            <div className="flex flex-col items-center gap-2 text-slate-600">
              <div className="h-8 w-8 rounded-full border-2 border-slate-700 border-t-cyan-400 animate-spin" />
              <span className="text-[10px] font-mono tracking-wider uppercase">
                Lámina {String(current + 1).padStart(2, '0')}
              </span>
            </div>
          </div>

          {loadedRange.has(current) || current === 0 ? (
            <Image
              src={`${CDN_BASE}/decks/slides/slide_${String(current + 1).padStart(2, '0')}.webp`}
              alt={`Lámina ${current + 1} — ${currentSlide?.title ?? ''}`}
              fill
              // QA-E2E-016 fix: unoptimized=true bypasses the Next.js image
              // optimizer (/_next/image?url=...). The slides are already
              // WebP (optimized at build time). Going through the optimizer
              // adds latency on each navigation because the optimizer URL
              // has its own cache, separate from the <link rel="preload">
              // cache. With unoptimized, the browser fetches the WebP
              // directly from /decks/slides/ — same URL as the preload link
              // → instant cache hit.
              unoptimized
              priority
              sizes="(max-width: 768px) 100vw, 1280px"
              className="object-contain transition-opacity duration-200"
              quality={85}
              onLoad={() => {
                setImageLoaded(true);
                // QA-E2E-016: mark this slide as ever-loaded so future
                // navigations skip the skeleton.
                setEverLoaded((prev) => new Set(prev).add(current));
              }}
            />
          ) : (
            <div className="flex items-center justify-center text-slate-600">
              <div className="animate-pulse">Cargando…</div>
            </div>
          )}
        </div>

        {/* Section + title overlay (bottom-left, fades in) */}
        <div className="absolute bottom-3 left-4 right-4 md:left-6 md:right-6 pointer-events-none z-10">
          <div className="flex items-end justify-between gap-4">
            <div className="bg-slate-950/80 backdrop-blur-sm rounded-lg px-3 py-2 border border-slate-700/50 max-w-2xl">
              <div className="text-[10px] font-semibold tracking-wider uppercase text-cyan-300 mb-0.5">
                {currentSlide?.section}
              </div>
              <div className="text-xs md:text-sm text-slate-100 font-medium line-clamp-2">
                {currentSlide?.title}
              </div>
 {/* (Sprint 103 — Casa Unificada): disclaimer inline
                  fuerte en slide 9. Fórmula canónica verbatim /legal/compensacion:
                  "ejemplo matemático, NO una promesa de ganancia".
                  Reemplaza el disclaimer débil anterior ("Proyección teórica
                  basada en matriz completa") por uno más explícito y legalmente
                  más fuerte. Visible directamente en el slide (no solo en
                  el footer de la página). */}
              {currentSlide?.n === 9 && (
                <div className="mt-1.5 pt-1.5 border-t border-amber-500/40">
                  <p className="text-[9px] md:text-[10px] text-amber-300 leading-snug font-semibold">
                    * Ejemplo matemático bajo hipótesis de ocupación completa.
                    NO es una promesa de ganancia. Los resultados reales dependen
                    del esfuerzo, la red y el compromiso.
                  </p>
                </div>
              )}
            </div>
            <div className="bg-slate-950/80 backdrop-blur-sm rounded-lg px-2.5 py-1.5 border border-slate-700/50 flex-shrink-0">
              <span className="text-xs font-mono tabular-nums text-slate-300">
                <span className="text-white font-bold">{String(current + 1).padStart(2, '0')}</span>
                <span className="text-slate-500"> / {String(total).padStart(2, '0')}</span>
              </span>
            </div>
          </div>
        </div>

        {/* Prev / Next buttons */}
        <button
          type="button"
          onClick={goPrev}
          disabled={current === 0}
          aria-label="Lámina anterior"
          className="absolute left-2 top-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full bg-slate-900/80 hover:bg-blue-600 border border-slate-700 hover:border-blue-500 backdrop-blur-sm transition-all flex items-center justify-center text-slate-200 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button
          type="button"
          onClick={goNext}
          disabled={current === total - 1}
          aria-label="Lámina siguiente"
          className="absolute right-2 top-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full bg-slate-900/80 hover:bg-blue-600 border border-slate-700 hover:border-blue-500 backdrop-blur-sm transition-all flex items-center justify-center text-slate-200 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      </div>

      {/* ─── TOOLBAR ──────────────────────────────────────────────────────── */}
      <div className="absolute top-2 right-2 z-20 flex items-center gap-1.5 bg-slate-900/80 backdrop-blur-sm rounded-lg p-1 border border-slate-700/50">
        <ToolbarButton
          onClick={() => setIsPlaying((p) => !p)}
          active={isPlaying}
          label={isPlaying ? 'Pausar (Espacio)' : 'Reproducir (Espacio)'}
        >
          {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </ToolbarButton>

        <ToolbarButton
          onClick={() => setZoom((z) => Math.max(1, z - 0.25))}
          disabled={zoom <= 1}
          label="Alejar"
        >
          <ZoomOut className="h-4 w-4" />
        </ToolbarButton>
        <span className="text-[10px] font-mono tabular-nums text-slate-400 px-1 min-w-[36px] text-center">
          {Math.round(zoom * 100)}%
        </span>
        <ToolbarButton
          onClick={() => setZoom((z) => Math.min(2, z + 0.25))}
          disabled={zoom >= 2}
          label="Acercar"
        >
          <ZoomIn className="h-4 w-4" />
        </ToolbarButton>

        <div className="h-4 w-px bg-slate-700" />

        <ToolbarButton
          onClick={() => setThumbOpen((t) => !t)}
          active={thumbOpen}
          label="Tira de láminas"
        >
          <List className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton onClick={() => setGridOpen(true)} label="Vista de grilla (G)">
          <Grid3x3 className="h-4 w-4" />
        </ToolbarButton>

        <ToolbarButton onClick={toggleFullscreen} active={isFullscreen} label="Pantalla completa (F)">
          {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </ToolbarButton>

        {/* ─── AUDIO CONTROLS (SPEC: audio por lámina con autoplay) ────
            3 botones:
              1. Toggle on/off (activa o pausa el audio — pausa el autoplay)
              2. Mute (silencia pero sigue reproduciendo para no perder lugar)
              3. Volume slider (0-1)
        */}
        <div className="h-4 w-px bg-slate-700" />

        <ToolbarButton
          onClick={() => setAudioEnabled((v) => !v)}
          active={audioEnabled}
          label={audioEnabled ? 'Audio activado (silenciar)' : 'Audio desactivado (activar)'}
        >
          {audioEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
        </ToolbarButton>

        <ToolbarButton
          onClick={() => setIsMuted((m) => !m)}
          active={isMuted}
          disabled={!audioEnabled}
          label={isMuted ? 'Activar sonido' : 'Silenciar'}
        >
          {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </ToolbarButton>

        {/* Volume slider — solo visible en hover de la toolbar (desktop) */}
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={audioVolume}
          onChange={(e) => setAudioVolume(Number(e.target.value))}
          className="w-12 h-1 accent-cyan-400 cursor-pointer hidden md:block"
          aria-label="Volumen del audio"
          disabled={!audioEnabled}
        />
      </div>

      {/* ─── THUMBNAIL RAIL (toggle) ──────────────────────────────────────── */}
      {thumbOpen && (
        <div className="absolute bottom-12 left-0 right-0 z-20 bg-slate-950/95 backdrop-blur border-t border-slate-800 px-3 py-2 overflow-x-auto">
          <div className="flex gap-1.5 min-w-min">
            {slides.map((s, i) => (
              <button
                key={s.n}
                type="button"
                onClick={() => {
                  goTo(i);
                  setThumbOpen(false);
                }}
                className={`flex-shrink-0 h-12 w-20 rounded border-2 transition-all overflow-hidden ${
                  i === current
                    ? 'border-cyan-400 ring-2 ring-cyan-400/30'
                    : 'border-slate-700 hover:border-slate-500'
                }`}
                title={`${s.n}. ${s.title}`}
              >
                <Image
                  src={`${CDN_BASE}/decks/slides/slide_${String(s.n).padStart(2, '0')}.webp`}
                  alt={`Lámina ${s.n}`}
                  width={80}
                  height={48}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ─── GRID OVERLAY (G key) ────────────────────────────────────────── */}
      {gridOpen && (
        <div className="absolute inset-0 z-30 bg-slate-950/95 backdrop-blur-md overflow-auto p-4 md:p-8">
          <div className="flex items-center justify-between mb-4 sticky top-0 bg-slate-950/80 backdrop-blur py-2">
            <h3 className="text-lg font-bold text-white">Todas las láminas</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setGridOpen(false)}
              className="text-slate-300 hover:text-white"
            >
              <X className="h-4 w-4 mr-1" /> Cerrar
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 pb-4">
            {slides.map((s, i) => (
              <button
                key={s.n}
                type="button"
                onClick={() => {
                  goTo(i);
                  setGridOpen(false);
                }}
                className="group text-left"
              >
                <div
                  className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                    i === current
                      ? 'border-cyan-400 shadow-lg shadow-cyan-500/30'
                      : 'border-slate-800 group-hover:border-slate-600'
                  }`}
                >
                  <Image
                    src={`${CDN_BASE}/decks/slides/slide_${String(s.n).padStart(2, '0')}.webp`}
                    alt={`Lámina ${s.n} — ${s.title}`}
                    fill
                    className="object-cover transition-transform group-hover:scale-105"
                    sizes="(max-width: 768px) 50vw, 200px"
                    loading="lazy"
                  />
                  <div className="absolute top-1 left-1 bg-slate-950/80 backdrop-blur-sm rounded px-1.5 py-0.5">
                    <span className="text-[10px] font-mono tabular-nums text-cyan-300">
                      {String(s.n).padStart(2, '0')}
                    </span>
                  </div>
                </div>
                <div className="mt-1.5 text-[11px] text-slate-400 group-hover:text-cyan-300 line-clamp-1 transition-colors">
                  {s.title}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ─── KEYBOARD HINTS (only on hover/focus, fades out) ─────────────── */}
      <div className="absolute bottom-2 left-2 z-20 text-[10px] text-slate-500 bg-slate-900/60 backdrop-blur rounded px-2 py-1 opacity-0 hover:opacity-100 transition-opacity">
        <kbd className="text-slate-300 font-mono">←→</kbd> navegar ·{' '}
        <kbd className="text-slate-300 font-mono">Espacio</kbd> play/pause ·{' '}
        <kbd className="text-slate-300 font-mono">F</kbd> fullscreen ·{' '}
        <kbd className="text-slate-300 font-mono">G</kbd> grilla ·{' '}
        <kbd className="text-slate-300 font-mono">1-9</kbd> saltar
      </div>

      {/* ─── HIDDEN PRELOADER ─────────────────────────────────────────────
          Renders all slides in `loadedRange` (except the current one, which
          is rendered visibly above) as 0×0 invisible Next/Image elements.
          This triggers the browser's HTTP cache for these PNGs so that
          when the user navigates, the image is already in memory and
          appears instantly. Next/Image dedupes requests, so these
          hidden pre-fetches don't duplicate the visible image's request.

          Why not just use <link rel="preload">? Because Next/Image
          optimization (resize, webp/avif) needs to go through the Next
          image optimizer, not raw <link> preload. This invisible Image
          approach reuses the same optimization pipeline. */}
      <div
        aria-hidden
        style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden', opacity: 0, pointerEvents: 'none' }}
      >
        {Array.from(loadedRange).map((idx) => {
          if (idx === current || idx < 0 || idx >= total) return null;
          return (
            <Image
              key={`preload-${idx}`}
              src={`${CDN_BASE}/decks/slides/slide_${String(idx + 1).padStart(2, '0')}.webp`}
              alt=""
              width={1}
              height={1}
              // QA-E2E-016 fix: unoptimized + loading=eager but NO priority.
              // The hidden preloader fetches all slides in the background
              // so they're in the browser HTTP cache when the user navigates.
              // Using unoptimized ensures the preloader fetches the SAME URL
              // as the visible slide (no optimizer URL mismatch).
              // Removed priority — only the visible slide should have priority
              // to avoid confusing the browser's resource prioritization.
              unoptimized
              loading="eager"
              sizes="1px"
              quality={80}
              onLoad={() => {
                // Mark as ever-loaded so future navigation skips skeleton.
                setEverLoaded((prev) => new Set(prev).add(idx));
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

function ToolbarButton({
  children,
  onClick,
  active,
  disabled,
  label,
}: {
  children: React.ReactNode;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={`h-7 w-7 rounded flex items-center justify-center transition-all disabled:opacity-30 disabled:cursor-not-allowed ${
        active
          ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40'
          : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
      }`}
    >
      {children}
    </button>
  );
}
